import React, { useState, useEffect } from 'react';
import './Home.css';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

const CAROUSEL_MESSAGES = [
    {
        type: 'tip',
        iconSrc: '/assets/icon-full-file.png',
        title: 'Auto-Tailored Resumes',
        content: 'Launchway rewrites your resume per job posting',
        subtitle: 'Each application gets a uniquely optimized version of your resume via LaTeX'
    },
    {
        type: 'tip',
        iconSrc: '/assets/icon-full-search.png',
        title: 'Smart Job Discovery',
        content: 'Scans multiple job boards automatically',
        subtitle: 'LinkedIn, Indeed, Glassdoor, and more — all from one CLI command'
    },
    {
        type: 'tip',
        iconSrc: '/assets/icon-full-terminal.png',
        title: 'One Command, Full Pipeline',
        content: 'Run your entire job search from terminal',
        subtitle: 'Discover, filter, tailor, and apply — Launchway handles the workflow end to end'
    },
    {
        type: 'tip',
        iconSrc: '/assets/icon-full-sort-descending.png',
        title: 'Relevance Scoring',
        content: 'Every job is scored against your profile',
        subtitle: 'Focus your energy on roles that actually match your skills and preferences'
    },
    {
        type: 'tip',
        iconSrc: '/assets/icon-full-ai.png',
        title: 'Form Filling Automation',
        content: 'Application forms filled from your profile',
        subtitle: 'Launchway maps your data to ATS fields so you skip the repetitive typing'
    },
];

const CAROUSEL_SLIDE_INTERVAL_MS = 6000;

const MotivationalCarousel = () => {
    const [currentMessageIndex, setCurrentMessageIndex] = useState(() => {
        return Math.floor(Math.random() * CAROUSEL_MESSAGES.length);
    });
    /** idle: visible | exiting: fade out | entering: fade in after slide change */
    const [phase, setPhase] = useState('idle');

    useEffect(() => {
        const intervalId = setInterval(() => {
            setPhase((p) => (p === 'idle' ? 'exiting' : p));
        }, CAROUSEL_SLIDE_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, []);

    const handleCardAnimationEnd = (e) => {
        if (e.target !== e.currentTarget) return;
        const { animationName } = e;
        if (animationName === 'carouselCardFadeOut') {
            setCurrentMessageIndex((prev) => (prev + 1) % CAROUSEL_MESSAGES.length);
            setPhase('entering');
        } else if (animationName === 'carouselCardFadeIn') {
            setPhase('idle');
        }
    };

    const currentMessage = CAROUSEL_MESSAGES[currentMessageIndex];

    const cardClassName = [
        'carousel-card',
        phase === 'exiting' && 'carousel-card--exit',
        phase === 'entering' && 'carousel-card--enter'
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className='motivational-carousel'>
            <div className='motivation-header'>
                <span className='motivation-label'>What Launchway Does</span>
            </div>
            <div
                className={cardClassName}
                onAnimationEnd={handleCardAnimationEnd}
            >
                <img
                    className='card-icon'
                    src={currentMessage.iconSrc}
                    alt={`${currentMessage.title} icon`}
                />
                <div className='card-content'>
                    <h3 className='card-title'>{currentMessage.title}</h3>
                    <p className='card-text'>{currentMessage.content}</p>
                    {currentMessage.subtitle && (
                        <p className='card-subtitle'>{currentMessage.subtitle}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

const Home = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [navScrolled, setNavScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setNavScrolled(window.scrollY > 24);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        // Fetch fresh user data from DB — never rely on stale localStorage user object
        fetch('/api/auth/verify', {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => {
                if (!data.success || !data.user) {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                    return;
                }
                // Keep localStorage cache in sync
                localStorage.setItem('user', JSON.stringify(data.user));
                setUser(data.user);
                setIsAuthenticated(true);
            })
            .catch(() => {
                // Network error — fall back to cached value for display only
                const raw = localStorage.getItem('user');
                if (raw) {
                    try { setUser(JSON.parse(raw)); setIsAuthenticated(true); } catch {}
                }
            });

        // Admin check is a separate concern — keep it independent
        fetch('/api/credits', {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => setIsAdmin(Boolean(data?.credits?.is_admin)))
            .catch(() => setIsAdmin(false));
    }, []);

    const handleLogout = () => {
        const token = localStorage.getItem('auth_token');

        // Clear authentication data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        setUser(null);
        navigate('/features-of-launchway');
        setIsAuthenticated(false);

        // Optionally call the logout endpoint
        if (token) {
            fetch('/api/auth/logout', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }).catch(err => console.log('Logout API call failed:', err));
        }
    };

    return (
        <div className='home-container'>
            {/* Top Navigation Bar */}
            <nav className={`top-nav${navScrolled ? ' top-nav-scrolled' : ''}`}>
                <div className='top-nav-inner'>
                    <div className='nav-title'>
                        <img src='/assets/launchwaylogo.png' alt='Launchway Logo' className='nav-logo' />
                        <h2 className='app-title'>
                            Launch<span className='app-title-accent'>way</span>
                        </h2>
                    </div>
                    <div className='nav-user'>
                        {isAuthenticated ? (
                            <>
                                <div className='user-info'>
                                    <span className='user-greeting'>Welcome back, <strong>{user?.first_name}!</strong></span>
                                </div>
                                <button className='logout-btn' onClick={handleLogout}>Logout</button>
                            </>
                        ) : (
                            <>
                                <button className='login-btn' onClick={() => navigate('/login')}>Login</button>
                                <button className='signup-btn' onClick={() => navigate('/signup')}>Sign Up</button>
                            </>
                        )}
                    </div>
                </div>
            </nav>

            {/* Main Content: Conditional View Based on Auth */}
            {!isAuthenticated ? (
                /* Unauthenticated View: Constellation Journey */
                <div className='constellation-view'>
                    <div className='constellation-container'>
                        <div className='your-star'>
                            <div className='star-glow'>⭐</div>
                            <span className='star-label'>You</span>
                        </div>
                        <div className='opportunity-stars'>
                            <div className='opp-star opp-1'>✨</div>
                            <div className='opp-star opp-2'>✨</div>
                            <div className='opp-star opp-3'>✨</div>
                        </div>
                        <svg className='connection-lines' viewBox='0 0 800 600'>
                            <line className='line-1' x1='400' y1='300' x2='250' y2='140' stroke='rgba(255, 215, 0, 0.3)' strokeWidth='2' strokeDasharray='5,5' />
                            <line className='line-2' x1='400' y1='300' x2='600' y2='150' stroke='rgba(255, 215, 0, 0.3)' strokeWidth='2' strokeDasharray='5,5' />
                            <line className='line-3' x1='400' y1='300' x2='560' y2='470' stroke='rgba(255, 215, 0, 0.3)' strokeWidth='2' strokeDasharray='5,5' />
                        </svg>
                    </div>
                    <div className='journey-prompt'>
                        <h1 className='journey-title'>
                            <span className='title-primary'>Download The</span>{' '}
                            <span className='title-accent'>Launchway CLI</span>
                        </h1>
                        <p className='journey-tagline'>The CLI is the real product</p>
                        <p className='journey-description'>
                            Launchway runs locally from your terminal and automates your job search workflow.
                            Start by downloading the CLI, then sign in and connect your profile.
                        </p>
                        <div className='journey-cta'>
                            <button className='cta-primary' onClick={() => navigate('/download')}>
                                Download Launchway CLI
                            </button>
                            <button className='cta-secondary' onClick={() => navigate('/signup')}>
                                Create Account
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                /* Authenticated View: Action Buttons */
                <div className='main-content'>
                    {/* Hero Section with Carousel */}
                    <div className='hero-section-wrapper'>
                        <div className='hero-text'>
                            <h1 className='main-title'>
                                <span className='title-primary'>Launch</span>
                                <span className='title-accent'>way</span>
                            </h1>
                            <p className='tagline'>Install the CLI first. That is the core product.</p>
                            <p className='description'>
                                Launchway CLI is where job discovery and automation happen.
                                Web app supports setup, credits, and account management.
                            </p>
                            <button
                                className='hero-primary-cta'
                                onClick={() => navigate('/download')}
                            >
                                Download Launchway CLI
                            </button>
                        </div>

                        {/* Motivational Carousel - Right Side on Desktop */}
                        <MotivationalCarousel />
                    </div> {/* End hero-section-wrapper */}

                    {/* Action List Section */}
                    <div className='actions-section'>
                        <div className='actions-header'>
                            <h2 className='actions-title'> Quick Actions</h2>
                            <p className='actions-subtitle'>Choose an action to get started</p>
                        </div>
                        <div className='action-list'>

                    <button
                        className='action-btn action-btn--download action-btn--primary-product'
                        onClick={() => navigate('/download')}
                    >
                        <div className='btn-content'>
                            <span className='btn-title'>Download Launchway CLI</span>
                            <span className='btn-subtitle'>Start here: install the CLI, sign in, and run your automated job search from terminal</span>
                            <div className='btn-status'>
                                <span className='status-text status-text--highlight'>v0.2.42· Python 3.11+</span>
                            </div>
                        </div>
                    </button>

                    <button
                        className='action-btn'
                        onClick={() => navigate('/profile')}
                    >
                        <div className='btn-content'>
                            <span className='btn-title'>Manage Profile</span>
                            <span className='btn-subtitle'>Update your information and preferences</span>
                        </div>
                    </button>

                    <button
                        className='action-btn'
                        onClick={() => navigate('/manage-credits')}
                    >
                        <div className='btn-content'>
                            <span className='btn-title'>Manage Credits</span>
                            <span className='btn-subtitle'>View your subscription, usage limits, and remaining credits</span>
                            <div className='btn-status'>
                                <span className='status-text'>Track daily usage & limits</span>
                            </div>
                        </div>
                    </button>

                    <button
                        className='action-btn'
                        onClick={() => navigate('/report-bug')}
                    >
                        <div className='btn-content'>
                            <span className='btn-title'>Report Bug + Beta Feedback</span>
                            <span className='btn-subtitle'>Submit reproducible issues for admin review and earn bonus limits when approved</span>
                            <div className='btn-status'>
                                <span className='status-text'>Approval-based rewards by severity</span>
                            </div>
                        </div>
                    </button>

                    {isAdmin && (
                        <button
                            className='action-btn'
                            onClick={() => navigate('/admin/moderation')}
                        >
                            <div className='btn-content'>
                                <span className='btn-title'>Admin Moderation</span>
                                <span className='btn-subtitle'>Review beta requests and bug bounty reports</span>
                                <div className='btn-status'>
                                    <span className='status-text'>Approve / reject submissions</span>
                                </div>
                            </div>
                        </button>
                    )}

                </div>
            </div>
            </div>
            )}

            {/* Temporarily hidden - uncomment to show videos
            { (
            <div className='features-section'>
                <div className='feature-card'>
                    <div className='feature-placeholder video-placeholder'>
                        <iframe
                            width="100%"
                            height="100%"
                            src="https://www.youtube.com/embed/MUuggii8MAk?si=Ru0ZiihOA274vA4Z"
                            title="Product Demo Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                    <h3>AI-Powered Job Discovery</h3>
                    <p>Find the perfect opportunities tailored to your skills and preferences</p>
                </div>

                <div className='feature-card'>
                    <div className='feature-placeholder video-placeholder'>
                        <iframe
                            width="100%"
                            height="100%"
                            src="https://www.youtube.com/embed/OqPWf4-73D4?si=_XFw9qkAQ32DDh8g"
                            title="Analytics Dashboard Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                    <h3>Track Your Progress</h3>
                    <p>Monitor applications and get insights on your job search performance</p>
                </div>
            </div>
            )}
            */}

            <Footer />
        </div>
    );
};

export default Home;